import dayjs from "dayjs";
import { useCalendarStore } from "../../store/useCalendarStore";
import { useEffect, useRef } from "react";
import { dragColumnSpan } from "./dragColumnSpan";
import { useTimeOffDrag } from "./useTimeOffDrag";
import { timeAtColumnOffset } from "./timeOffForm";
import { slotAtYOffset } from "./slotAtYOffset";

const useDrag = () => {
  const dragTimeoutRef = useRef(null);
  const dragTimedOutRef = useRef(false);

  const slotHeight = useCalendarStore((state) => state.calendarData.slotHeight);
  const startTime = useCalendarStore((state) => state.calendarData.startTime);
  const timeStep = useCalendarStore((state) => state.calendarData.timeStep);

  const setRescheduleData = useCalendarStore(
    (state) => state.setRescheduleData,
  );
  const { draggingId, setDraggingId, setLastClickYOffset } = useCalendarStore(
    (state) => ({
      draggingId: state.draggingId,
      setDraggingId: state.setDraggingId,
      setLastClickYOffset: state.setLastClickYOffset,
    }),
  );
  const updateAppointmentInCalendar = useCalendarStore(
    (state) => state.updateAppointmentInCalendar,
  );
  const { handleDrop: handleTimeOffDrop } = useTimeOffDrag();

  useEffect(() => {
    return () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
        dragTimeoutRef.current = null;
      }
    };
  }, []);

  function cleanupDragState() {
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
      dragTimeoutRef.current = null;
    }

    setDraggingId(null);
    unHoverAll();
  }

  function handleDragStart(event) {
    // A time off carries no appointment to track; its drop is resolved whole in
    // handleDragEnd. The timeout flag still has to be cleared, or a previous
    // appointment drag that timed out would make this drop return early.
    if (event.active.data.current?.timeOff) {
      dragTimedOutRef.current = false;
      return;
    }

    const { appointmentId } = event.active.data.current.appointment;
    setDraggingId(appointmentId);
    dragTimedOutRef.current = false;

    // Dispatch a synthetic mouseup event to force drag timeout after 10 seconds.
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
    }
    dragTimeoutRef.current = setTimeout(() => {
      dragTimedOutRef.current = true;
      setDraggingId(null);
      unHoverAll();
      document.dispatchEvent(new MouseEvent("mouseup", { bubbles: true }));
    }, 10000);
  }

  function handleDragEnd(event) {
    cleanupDragState();

    // If the drag timed out, cancel any reschedule logic.
    if (dragTimedOutRef.current) {
      setLastClickYOffset(0);
      return;
    }

    const { active, over } = event;

    // A time off is resolved whole from where it landed: the column it was
    // dropped on gives the day and the target, and how far down the block sits
    // inside that column gives the new start.
    const timeOff = active?.data.current?.timeOff;
    if (timeOff) {
      const droppedTop = active?.rect.current.translated?.top;
      if (over && droppedTop != null) {
        const { date, userId, resourceId } = over.data.current;

        void handleTimeOffDrop({
          ...timeOff,
          date,
          startTime: timeAtColumnOffset({
            dayStartTime: startTime,
            yOffset: droppedTop - over.rect.top,
            slotHeight,
            timeStep,
          }),
          userId: userId ?? null,
          resourceId: resourceId ?? null,
        });
      }
      setLastClickYOffset(0);
      return;
    }

    if (over && active?.id !== over?.id) {
      const appointment = active.data.current.appointment;
      // A drop can fire for an active draggable that carries no appointment —
      // the block unmounted mid-drag (e.g. a calendar refetch) and dnd-kit kept
      // a data ref with neither payload. There is nothing to reschedule, so
      // revert instead of crashing on appointment.date (Sentry LIME-CLIENT-15T).
      if (!appointment) {
        setLastClickYOffset(0);
        return;
      }
      const originalDate = appointment.date;
      const originalYOffset = appointment.yOffset;
      const originalStartTime = appointment.startTime;

      const newDate = over.data.current.date;
      const newStartTime = appointment.currentDragTime;
      // A drop can fire without a tracked drag time (the drag never produced a
      // target slot); there is nothing to reschedule, so revert instead of
      // crashing in calculateYOffsetForTime (Sentry LIME-CLIENT-62).
      if (newStartTime == null) {
        setLastClickYOffset(0);
        return;
      }
      const yOffset = calculateYOffsetForTime(newStartTime);

      appointment.yOffset = yOffset;
      appointment.date = newDate;
      appointment.formattedData.date = dayjs(newDate).format("DD. MM. YYYY");
      appointment.startTime = newStartTime;
      appointment.formattedData.startTime = newStartTime;

      const { currentResources, currentUsers } = active.data.current;
      const { resources, userIds: users } = appointment;

      let isValidDrop = true;

      // Validate resource collisions, if any.
      if (currentResources) {
        appointment.oldResources = [...appointment.resources];
        const allCollisions = [...event.collisions].sort(
          (a, b) => b.data.value - a.data.value,
        );
        // Slice based on how many resources were originally on the appointment.
        const collisionsSliced = allCollisions.slice(
          0,
          currentResources.length,
        );
        const firstCollision = collisionsSliced.at(0);
        const collisionDate =
          firstCollision?.data.droppableContainer.data.current.date;
        const allOnSameDate =
          firstCollision != null &&
          collisionsSliced.every(
            (c) =>
              c.data.droppableContainer.data.current.date === collisionDate,
          );
        if (!allOnSameDate) {
          isValidDrop = false;
        } else {
          const newResources = resources
            .filter((r) => !currentResources.includes(r))
            .concat(
              collisionsSliced.map(
                (c) => c.data.droppableContainer.data.current.resourceId,
              ),
            );
          appointment.resources = [...new Set(newResources)];
        }
      }

      // Validate user collisions, if any.
      if (isValidDrop && currentUsers) {
        const allCollisions = [...event.collisions].sort(
          (a, b) => b.data.value - a.data.value,
        );
        const collisionsSliced = allCollisions.slice(0, currentUsers.length);
        const firstCollision = collisionsSliced.at(0);
        const collisionDate =
          firstCollision?.data.droppableContainer.data.current.date;
        const allOnSameDate =
          firstCollision != null &&
          collisionsSliced.every(
            (c) =>
              c.data.droppableContainer.data.current.date === collisionDate,
          );
        if (!allOnSameDate) {
          isValidDrop = false;
        } else {
          const newUsers = users
            .filter((u) => !currentUsers.includes(u))
            .concat(
              collisionsSliced.map(
                (c) => c.data.droppableContainer.data.current.userId,
              ),
            );
          appointment.users = [...new Set(newUsers)];
        }
      }

      // If the drop is invalid, revert the appointment to its original values.
      if (!isValidDrop) {
        appointment.yOffset = originalYOffset;
        appointment.date = originalDate;
        appointment.formattedData.date =
          dayjs(originalDate).format("DD. MM. YYYY");
        appointment.startTime = originalStartTime;
        appointment.formattedData.startTime = originalStartTime;
        setLastClickYOffset(0);
        return;
      }

      const updatedAppointment = {
        ...appointment,
        users: appointment.users || appointment.userIds || [],
        userIds: appointment.userIds || [],
        resources: appointment.resources || [],
        date: newDate,
        startTime: newStartTime,
        formattedData: {
          ...appointment.formattedData,
          date: dayjs(newDate).format("DD. MM. YYYY"),
          startTime: newStartTime,
        },
        yOffset: yOffset,
      };

      updateAppointmentInCalendar(updatedAppointment);

      setRescheduleData({
        appointment: updatedAppointment,
        previousDate: originalDate,
        previousStartTime: originalStartTime,
        sendNotifications: false,
      });
    }
    setLastClickYOffset(0);
  }

  function handleDragCancel() {
    cleanupDragState();
    setLastClickYOffset(0);
  }

  function calculateTimeForYOffset(yOffset) {
    return slotAtYOffset({ yOffset, slotHeight, startTime, timeStep }).time;
  }

  function calculateTimeForDragYOffset(yOffset) {
    if (yOffset < 0) {
      yOffset = 0;
    }
    const rowIndex = yOffset / slotHeight;
    let time = dayjs(startTime, "HH:mm").add(rowIndex * timeStep, "minute");
    const timeOffset = Math.round(time.minute() / 5) * 5 - time.minute();
    time = time.add(timeOffset, "minute");
    return time.format("HH:mm");
  }

  function calculateYOffsetForTime(time) {
    const [hour, minute] = time.split(":").map(Number);
    const [startHour, startMinute] = startTime.split(":").map(Number);
    const minutesFromStart = (hour - startHour) * 60 + (minute - startMinute);
    const rowIndex = minutesFromStart / timeStep;
    return rowIndex * slotHeight;
  }

  function handleDragMove(event) {
    const { collisions } = event;
    unHoverAll();
    const { active } = event;
    if (!collisions || collisions.length < 1) return;

    // A time off occupies one column, so the strongest collision is the target
    // outright — there is no multi-column span to slice or validate, and it
    // carries none of the appointment fields the rest of this reads.
    if (active.data.current?.timeOff) {
      const target = [...collisions]
        .sort((a, b) => b.data.value - a.data.value)
        .at(0);
      target?.data.droppableContainer.node.current?.classList.add("hovering");
      return;
    }

    const { currentResources, currentUsers } = active.data.current;
    // Both are absent for a block rendered without a resource or user order, and
    // handleDragEnd already tolerates that; mirror it here instead of crashing.
    const columnSpan = dragColumnSpan(currentResources, currentUsers);
    if (columnSpan == null) return;

    const allCollisions = collisions.sort(
      (a, b) => b.data.value - a.data.value,
    );

    // take as many resource collisions as there were resources on initial appointment
    const collisionsSliced = allCollisions.slice(0, columnSpan);
    const newDate =
      collisionsSliced.at(0).data.droppableContainer.data.current.date;
    active.data.current.appointment.hoverDate = newDate;
    const allOnSameDate = collisionsSliced.every(
      (c) => c.data.droppableContainer.data.current.date === newDate,
    );
    if (!allOnSameDate) {
      return false;
    }
    for (const c of collisionsSliced) {
      c.data.droppableContainer.node.current.classList.add("hovering");
    }
  }

  function unHoverAll() {
    const allCurrentHovered = document.getElementsByClassName("hovering");
    for (const item of [...allCurrentHovered]) {
      item.classList.remove("hovering");
    }
  }

  return {
    handleDragEnd,
    calculateTimeForYOffset,
    calculateTimeForDragYOffset,
    calculateYOffsetForTime,
    handleDragMove,
    handleDragStart,
    handleDragCancel,
    draggingId,
  };
};

export default useDrag;
